import { GET_SITES } from "@/gql/site";
import { getStoredSelectedSite } from "@/utils/common";
import { handleApolloError } from "@/utils/handle-form";
import { ApolloQueryResult, useQuery } from "@apollo/client";
import {
  IGetSitesQuery,
  IGetSitesQueryVariables,
} from "@shared/common/types/api-private/generated/graphql";
import { ISite } from "@shared/common/types/site";
import { noopFunction } from "@shared/common/utils/helper";
import { getParamValue } from "@shared/common/utils/url";
import { useSession } from "next-auth/react";
import { NextRouter, useRouter } from "next/router";
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useOrganizationContext } from "./organization";

interface ISitesContext {
  initialized: boolean;
  sites: ISite[] | null;
  currentSite: ISite | null;
  currentSiteId: string;
  setCurrentSite: Dispatch<SetStateAction<ISite | null>>;
  isLoading: boolean;
  siteNotfound: boolean;
  reload: () => Promise<ApolloQueryResult<IGetSitesQuery>>;
  reloadWithOrganization: () => Promise<unknown>;
}

export const FALLBACK_SITE_ID = "nosite";

const context = React.createContext<ISitesContext>({
  initialized: false,
  sites: null,
  setCurrentSite: noopFunction,
  currentSite: null,
  currentSiteId: FALLBACK_SITE_ID,
  isLoading: true,
  siteNotfound: false,
  reload: noopFunction,
  reloadWithOrganization: noopFunction,
});

interface ISitesProviderProps {
  children: ReactNode;
}

const getCurrentSitePublicId = (router: NextRouter) => {
  if (!router.pathname.includes("[site_public_id]")) {
    return null;
  }
  const siteId = getParamValue(router.query, "site_public_id");
  return siteId ?? FALLBACK_SITE_ID;
};

const SitesProvider: React.FC<ISitesProviderProps> = ({ children }) => {
  const [currentSite, setCurrentSite] = useState<ISite | null>(null);
  const [currentSiteLoading, setCurrentSiteLoading] = useState(true);

  const router = useRouter();
  const { data: session, status } = useSession();
  const {
    currentOrganization,
    organizations,
    reload: reloadOrganization,
  } = useOrganizationContext();

  const {
    data: sitesData,
    refetch,
    loading: sitesLoading,
  } = useQuery<IGetSitesQuery, IGetSitesQueryVariables>(GET_SITES, {
    skip: !session || !organizations?.[0]?.id,
    variables: {
      organizationId: currentOrganization?.id ?? organizations?.[0]?.id ?? "",
    },
    onError: handleApolloError,
  });

  const refetchWithOrg = async () => {
    await reloadOrganization();
    await refetch();
  };

  useEffect(() => {
    if (!sitesData) {
      return;
    }

    const currentSitePublicId = getCurrentSitePublicId(router);

    if (currentSitePublicId && currentSitePublicId != FALLBACK_SITE_ID) {
      setCurrentSite(
        (sitesData.sites.find(
          (site) => site.publicId === currentSitePublicId
        ) as ISite) ?? null
      );
      setCurrentSiteLoading(false);
      return;
    }

    setCurrentSite(
      (sitesData.sites.find(
        (site) => site.publicId === getStoredSelectedSite()
      ) as ISite) ??
        sitesData.sites[0] ??
        null
    );
    setCurrentSiteLoading(false);
  }, [currentSite, router, sitesData]);

  return (
    <context.Provider
      value={{
        initialized: true,
        sites: (sitesData?.sites as ISite[]) ?? null,
        currentSite,
        currentSiteId: currentSite?.publicId ?? FALLBACK_SITE_ID,
        setCurrentSite,
        // TODO: currentSiteLoading을 봐야하는 이유가 있는지 검토 필요(사유: loading 값이 site가 존재하는지에 의존성이 걸려있어 사이트 데이터가 존재하지 않을 때 해당 loading이 무조건 true임.)
        isLoading: status === "loading" || sitesLoading || currentSiteLoading,
        siteNotfound: Boolean(!sitesData?.sites),
        reload: refetch,
        reloadWithOrganization: refetchWithOrg,
      }}
    >
      {children}
    </context.Provider>
  );
};

export const useSitesContext = () => {
  const result = useContext(context);
  if (!result.initialized) {
    throw new Error("Site context must be used within a SiteProvider!");
  }
  return result;
};

export default SitesProvider;
